import { Briefcase, ChevronLeft, Edit2, Loader2, Plus, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ClientDeleteConfirm,
  ClientForm,
  ClientList,
  type Client,
  type CreateClientInput,
  type UpdateClientInput,
} from "@lexiun/org-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { PlatformApiError, type ClientDeleteCensusResponse } from "../lib/platform-api";

/**
 * Name the cases behind a 409 CONFLICT from the client DELETE. lexserver puts
 * them in `error.details.cases` as `[{caseId, title, storageShape}]`; the
 * envelope is typed as loose `Record<string, unknown>`, so this narrows
 * defensively and returns "" rather than inventing a count it cannot verify.
 */
function formatBlockingCases(details: Record<string, unknown> | undefined): string {
  const cases = details?.cases;
  if (!Array.isArray(cases) || cases.length === 0) return "";
  const titles = cases
    .map((c) => (c as { title?: unknown }).title)
    .filter((t): t is string => typeof t === "string");
  if (titles.length === 0) return "";
  const shown = titles.slice(0, 3).join(", ");
  // "and N more" counts against the CASES, not the titles we could read: a
  // blocker whose title is missing still blocks, and dropping it from the
  // count would under-report the work the operator has to do.
  const rest = Math.max(0, cases.length - 3);
  return `Blocking cases: ${shown}${rest > 0 ? ` and ${rest} more` : ""}.`;
}

/**
 * The non-`ready` census states. Matches `ClientDeleteConfirm`'s panel
 * geometry so the overlay does not jump when the census resolves, and
 * deliberately offers only a dismiss — there is no path to the destructive
 * call from any state where the impact is unknown.
 */
function DeleteNotice({
  testId,
  title,
  body,
  onClose,
  busy,
}: {
  testId: string;
  title: string;
  body: string;
  onClose: () => void;
  busy?: boolean;
}) {
  // Escape dismisses, matching `ClientDeleteConfirm` — otherwise the census
  // states the operator hits MOST (loading, backend-pending) would be the
  // only panels in this flow that trap the keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      className="w-full max-w-md rounded-lg border border-slate-300 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
        {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
        {title}
      </h2>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{body}</p>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          data-testid={`${testId}-close`}
          className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Close
        </button>
      </div>
    </div>
  );
}

/**
 * Clients admin page (Phase D/D2; P3′ management parity 2026-07-30).
 *
 * Mounted at `/orgs/:orgId/clients`. Lists every client in the org
 * via the Phase B lexserver routes (`/api/org/:orgId/clients`) and
 * renders the shared `@lexiun/org-admin` `ClientList` + `ClientForm`
 * primitives against real data. The create/edit/delete surface is
 * paired with a Playwright spec at `tests/e2e/clients.spec.ts`.
 *
 * DELETE (plan §2.4.7-8, `lexide-navigation-and-client-manager-miniplan.md`):
 * the shared three-variant `ClientDeleteConfirm` replaces the old
 * `window.confirm` + unguarded DELETE. Clients are the platform's one
 * hard-delete exception; referencing cases UNASSIGN to «Não atribuídos»
 * rather than being destroyed, and legacy-`storageShape` cases block.
 *
 * WHY THE CENSUS IS FETCHED LAZILY (and LexIDE fetches it eagerly): LexIDE's
 * dialog covers ONE client, so it can afford an eager fetch to label its
 * button. This is a LIST — an eager census per row would fire N requests of
 * several aggregate counts each, on exactly the hot path lexserver's own
 * route docstring says to keep them off. So the button opens the dialog, and
 * the DIALOG is the gate: nothing is deleted before the census answers.
 *
 * LexPortal is English-only and renders org-admin with no locale provider,
 * so the shared confirm falls back to en-US by design.
 */
export function Clients() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const api = usePlatformApi();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Delete flow ────────────────────────────────────────────────────────
  // `censusState` is deliberately THREE states, never collapsed to a
  // falsy-empty census (unknown-must-be-representable):
  //   "loading"        — asked, no answer yet. No claim made either way.
  //   "backend-pending" — the route 404s: lexserver's P1 guard is not on this
  //                       server, so a DELETE here would run the OLD unguarded
  //                       path and orphan case rows. Offer NO delete action.
  //   "failed"          — the census call errored. Same rule: no delete.
  //   census object     — a real answer; the shared confirm picks its variant.
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
  const [censusState, setCensusState] = useState<
    | { kind: "loading" }
    | { kind: "backend-pending" }
    | { kind: "failed"; error: string }
    | { kind: "ready"; census: ClientDeleteCensusResponse }
  >({ kind: "loading" });
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const res = await api.listClients(orgId);
      setClients(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [api, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (input: CreateClientInput | UpdateClientInput) => {
    setSubmitting(true);
    setError(null);
    try {
      // The create form only ever passes CreateClientInput (it renders
      // the Type radio group only in create mode), but the shared
      // primitive types the onSubmit signature as the union for edit
      // re-use — a runtime narrowing is therefore safe here.
      await api.createClient(orgId, input as CreateClientInput);
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to create client",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (input: CreateClientInput | UpdateClientInput) => {
    if (!editing) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.updateClient(orgId, editing.clientId, input as UpdateClientInput);
      setEditing(null);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to update client",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Monotonic token for the in-flight census request.
   *
   * Without it, open A → close → open B lets A's slower response land on B and
   * authorize B's delete against A's census — the same stale-response class the
   * git-browser guards with request tokens (`07eda3c08`), except here the
   * mispaint is a destructive call rather than a wrong diff.
   */
  const censusReqRef = useRef(0);

  /** Open the confirm and ask lexserver what deleting this client would cost. */
  const openDelete = useCallback(
    async (client: Client) => {
      // Defense-in-depth; the button is already hidden for the self-client,
      // and the server 403s it regardless.
      if (client.isSelfClient) return;
      const req = ++censusReqRef.current;
      setDeleteTarget(client);
      setCensusState({ kind: "loading" });
      setDeleteError(null);
      try {
        const census = await api.fetchClientDeleteCensus(orgId, client.clientId);
        if (req !== censusReqRef.current) return; // superseded — drop it
        setCensusState(
          census === null ? { kind: "backend-pending" } : { kind: "ready", census },
        );
      } catch (e) {
        if (req !== censusReqRef.current) return;
        setCensusState({
          kind: "failed",
          error:
            e instanceof PlatformApiError
              ? `${e.code}: ${e.message}`
              : e instanceof Error
                ? e.message
                : "Could not read the delete impact",
        });
      }
    },
    [api, orgId],
  );

  const closeDelete = useCallback(() => {
    // Invalidate any in-flight census so a late response cannot reopen or
    // repaint a dialog the operator has already dismissed.
    censusReqRef.current += 1;
    setDeleteTarget(null);
    setDeleteError(null);
    setDeleting(false);
    setCensusState({ kind: "loading" });
  }, []);

  const confirmDelete = useCallback(async () => {
    // Unreachable from the UI unless a real census said `deletable`, but the
    // guard is cheap and this is a destructive call.
    if (!deleteTarget || censusState.kind !== "ready" || !censusState.census.deletable) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteClient(orgId, deleteTarget.clientId);
      closeDelete();
      await load();
    } catch (e) {
      // A late 409 (a legacy case appeared between census and confirm) is
      // surfaced IN the dialog, not as a page-level error — the operator is
      // looking at the dialog. lexserver returns the blockers in
      // `details.cases` ([{caseId, title, storageShape}]); naming them beats
      // "CONFLICT", because the operator's next step is to go fix those cases.
      setDeleteError(
        e instanceof PlatformApiError
          ? [`${e.code}: ${e.message}`, formatBlockingCases(e.details)]
              .filter(Boolean)
              .join(" ")
          : e instanceof Error
            ? e.message
            : "Failed to delete client",
      );
    } finally {
      setDeleting(false);
    }
  }, [api, orgId, deleteTarget, censusState, closeDelete, load]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to={`/orgs/${orgId}`}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <ChevronLeft className="h-3 w-3" /> Back to org
          </Link>
          <h1 className="text-2xl font-semibold">Clients</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {loading
              ? "Loading..."
              : `${clients.length} client${clients.length === 1 ? "" : "s"} in org ${orgId}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          data-testid="clients-create-button"
        >
          <Plus className="h-4 w-4" />
          New client
        </button>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          data-testid="clients-error"
        >
          {error}
        </div>
      )}

      <div className="rounded border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        {/*
          No `onSelect` in D2: the detail page (`/orgs/:orgId/clients/:clientId`)
          lands in a later Phase D sub-step. Row clicks are therefore
          inert here except via the edit/delete action buttons.
        */}
        <ClientList
          clients={clients}
          loading={loading}
          renderActions={(client) => (
            <>
              <Link
                to={`/orgs/${orgId}/clients/${client.clientId}/matters`}
                onClick={(e) => e.stopPropagation()}
                title="View matters under this client"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid={`client-matters-${client.clientId}`}
              >
                <Briefcase className="h-3.5 w-3.5" />
              </Link>
              <Link
                to={`/orgs/${orgId}/clients/${client.clientId}/assignments`}
                onClick={(e) => e.stopPropagation()}
                title="Manage client assignments"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid={`client-assignments-${client.clientId}`}
              >
                <Users className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(client);
                }}
                title="Edit client"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid={`client-edit-${client.clientId}`}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </button>
              {!client.isSelfClient && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void openDelete(client);
                  }}
                  title="Delete client"
                  className="rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid={`client-delete-${client.clientId}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
        />
      </div>

      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent data-testid="clients-create-dialog">
          <DialogHeader>
            <DialogTitle>New client</DialogTitle>
            <DialogDescription>
              Create a client under the current org. Matters and cases are
              created separately under this client.
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="clients-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
            <DialogDescription>
              Update the client's name, status, or primary contact.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <ClientForm
              initial={editing}
              onSubmit={handleEdit}
              onCancel={() => setEditing(null)}
              submitting={submitting}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm. The shared component renders the PANEL only, so the
          host owns the overlay (same as LexIDE). Every non-`ready` census
          state renders an explanation and NO delete action — an operator must
          never be able to reach the destructive call through a state where we
          could not read the impact. */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="client-delete-overlay"
        >
          {censusState.kind === "ready" && censusState.census.deletable ? (
            <ClientDeleteConfirm
              client={deleteTarget}
              census={censusState.census}
              onConfirm={() => void confirmDelete()}
              onCancel={closeDelete}
              deleting={deleting}
              error={deleteError ?? undefined}
            />
          ) : censusState.kind === "ready" ? (
            // Not `deletable` and not legacy-blocked ⇒ the self-client 403,
            // which `classifyClientDelete` cannot see. Say so plainly instead
            // of rendering a confirm whose delete the server would reject.
            censusState.census.legacyCount > 0 ? (
              <ClientDeleteConfirm
                client={deleteTarget}
                census={censusState.census}
                onConfirm={() => void confirmDelete()}
                onCancel={closeDelete}
              />
            ) : (
              <DeleteNotice
                testId="client-delete-not-deletable"
                title="This client cannot be deleted"
                body="The server reports this client as not deletable — the organization's own self-client is protected."
                onClose={closeDelete}
              />
            )
          ) : censusState.kind === "loading" ? (
            <DeleteNotice
              testId="client-delete-loading"
              title="Checking impact…"
              body="Asking the server what deleting this client would affect."
              onClose={closeDelete}
              busy
            />
          ) : censusState.kind === "backend-pending" ? (
            <DeleteNotice
              testId="client-delete-backend-pending"
              title="Delete is not available yet"
              body="This lexserver does not expose the delete-impact check, which means it is still running the unguarded delete. Deleting now would leave this client's cases pointing at a client that no longer exists, so the action is disabled until the server is updated."
              onClose={closeDelete}
            />
          ) : (
            <DeleteNotice
              testId="client-delete-census-failed"
              title="Could not check the impact"
              body={`Delete is disabled because the impact check failed — ${censusState.error}`}
              onClose={closeDelete}
            />
          )}
        </div>
      )}

      {/* Guard for the case of a missing :orgId param — would only
          happen if this route is ever mounted without it. */}
      {!orgId && (
        <p className="text-sm text-red-500">
          <Link to="/orgs" className="underline">
            Select an organization
          </Link>{" "}
          to manage its clients.
        </p>
      )}
    </div>
  );
}
